import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { useAuth } from '@/hooks/useAuth';
import { billingApi } from '../api/billingApi';
import { getOrganizationId } from '../utils/billingOrganization';
import { useOnboarding } from './useOnboarding';
import type { BillingRedirectResponse } from '../types/billing';

export const billingKeys = {
  subscription: (organizationId?: string) =>
    ['billing-subscription', organizationId ?? 'none'] as const
};

export const useBillingStatus = () => {
  const { user } = useAuth();
  const organizationId = getOrganizationId(user);

  return useQuery({
    queryKey: billingKeys.subscription(organizationId),
    queryFn: () => billingApi.getSubscription(),
    enabled: Boolean(user && env.billingEnabled),
    networkMode: 'online',
    staleTime: 60 * 1000
  });
};

// `returnPath` decides where Stripe sends the user back. The default keeps the
// standalone billing page's success/cancel screens; the guided onboarding passes
// 'onboarding' so checkout returns into the unified flow (which then advances to
// the "ready to operate" step).
type CheckoutInput = { planCode?: string; lng: string; returnPath?: 'billing' | 'onboarding' };

export const useCreateCheckoutSession = () =>
  useMutation<BillingRedirectResponse, Error, CheckoutInput>({
    mutationFn: ({ planCode, lng, returnPath = 'billing' }) => {
      const base = `${window.location.origin}/${lng}`;
      const urls =
        returnPath === 'onboarding'
          ? {
              success_url: `${base}/onboarding?checkout=success`,
              cancel_url: `${base}/onboarding?checkout=cancel`
            }
          : {
              // Stripe replaces {CHECKOUT_SESSION_ID} with the real id on redirect.
              success_url: `${base}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
              cancel_url: `${base}/billing/cancel`
            };

      return billingApi.createCheckoutSession({
        plan_code: planCode || 'BASIC',
        ...urls
      });
    },
    onSuccess: (response) => {
      window.location.assign(response.url);
    }
  });

export const useCreateCustomerPortalSession = () => {
  const queryClient = useQueryClient();

  return useMutation<BillingRedirectResponse, Error, { lng: string }>({
    mutationFn: ({ lng }) =>
      billingApi.createCustomerPortalSession({
        return_url: `${window.location.origin}/${lng}/billing`
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      window.location.assign(response.url);
    }
  });
};

export const isBillingFrontendEnabled = () => env.billingEnabled;

/**
 * Checkout / Customer Portal are organization-admin only on the backend
 * (BillingPermission). Mirror that here so non-admins don't see actions that
 * would 403.
 *
 * Source of truth is onboarding's `is_billing_admin` (the BE's stable contract);
 * while it loads — or when billing is off — fall back to the role so admins
 * don't briefly lose their actions.
 */
export const useIsBillingAdmin = () => {
  const { user } = useAuth();
  const { data } = useOnboarding();

  if (data) {
    return data.is_billing_admin;
  }

  return user?.role === 'ORGANIZATION_ADMIN' || user?.role === 'PLATFORM_ADMIN';
};
